import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Echo from '@ably/laravel-echo';
import * as Ably from 'ably';
import { environment } from 'environments/environment';
import { LegFiJwtService } from './auth/legfi-jwt.service';
import { AuthService } from './auth/auth.service';

(window as any).Ably = Ably;

// Mirror GlobalErrorHandler's reporting window: reconnect/auth loops can fire the
// same error repeatedly, so skip identical messages seen within this window to
// keep an error storm from hammering POST /client-errors.
const REPORT_DEDUPE_MS = 10000;

// How long a tab may stay hidden before we disconnect its realtime socket to
// reclaim the concurrent connection (SD-7778). A backgrounded tab left open all
// day is the biggest reducible chunk of idle connections; five minutes is long
// enough that quick tab switches never drop realtime, short enough that a tab
// abandoned for the afternoon stops holding a connection. The socket is
// re-established (and all channels auto-resubscribed) the moment the tab is
// focused again.
//
// SCOPE (SD-7778): this reclaims HIDDEN tabs only — the window is armed purely
// off the Page Visibility API. A tab left VISIBLE but inactive is intentionally
// NOT disconnected: visible-idle reclamation would need DOM input-activity
// tracking and risks tearing down realtime a user is actively watching, for a
// much smaller slice of connections (the ticket's own analysis names hidden
// tabs as the reducible chunk). Visible-idle is out of scope pending PO
// confirmation.
const HIDDEN_IDLE_DISCONNECT_MS = 5 * 60 * 1000;

@Injectable({
    providedIn: 'root',
})
export class EchoService implements OnDestroy
{
    private _echo: Echo | null = null;
    private _activeChannels: Map<string, any> = new Map();
    private _isConnected$ = new BehaviorSubject<boolean>(false);
    private _destroy$ = new Subject<void>();

    // Fires after the shared Echo instance is torn down and rebuilt on session (re)establishment
    // (a login / re-login after logout), so non-org-scoped singletons that subscribed their
    // channel once (global-broadcast, notifications) can re-attach to the rebuilt instance.
    // destroy() runs first, so a subscriber's re-subscribe lands on a fresh instance (SD-7778).
    // Org-scoped consumers re-join per org context via OrgContextService.currentOrgId$ (SD-8086).
    private readonly _reestablished$ = new Subject<void>();
    private readonly _recentReports = new Map<string, number>();

    // True while the socket is intentionally disconnected for a hidden/idle tab,
    // so resumeConnection() only reconnects a connection we ourselves paused.
    private _paused = false;
    private _hiddenIdleTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * Bound so add/removeEventListener share one reference. On hide, arm the
     * idle-disconnect timer; on show, cancel it and resume a paused connection.
     */
    private readonly _onVisibilityChange = (): void => {
        if (document.visibilityState === 'hidden') {
            this.scheduleHiddenIdleDisconnect();
        } else {
            this.cancelHiddenIdleDisconnect();
            this.resumeConnection();
        }
    };

    /**
     * Emits after the shared Echo instance is torn down and ready to be rebuilt when a session
     * is (re)established after logout. Non-org-scoped singletons that subscribe their channel
     * only once (global-broadcast, notifications) must re-attach on this so realtime survives a
     * same-tab re-login (which does NOT reload the page). Ably re-attaches channels on
     * reconnect, but that does not help a consumer whose Echo instance was destroyed — it must
     * re-request its channel. Org-scoped consumers do NOT use this: they re-join per org context
     * via OrgContextService.currentOrgId$ (SD-8086).
     */
    public get reestablished$() {
        return this._reestablished$.asObservable();
    }

    constructor(
            private _authService: AuthService,
    ) {
        // Rebuild the shared instance when a session is (re)established after logout:
        // sessionStarted$ fires on a login / re-login (no prior token) into the same org,
        // where the socket was torn down at logout. destroy() then reestablished$ lets the
        // non-org-scoped consumers (global-broadcast, notifications) re-attach on the fresh
        // instance. Org-channel transitions (switch, PMC->child, fresh signup) are NOT handled
        // here: OrganizationBroadcastService owns those via OrgContextService.currentOrgId$
        // (leave/join the org channel, SD-8086). Tearing the whole instance down on an org
        // switch here would race that leave/join and drop the just-joined channel — so this
        // service reacts only to session (re)establishment, never to an in-session org switch.
        LegFiJwtService.sessionStarted$
                .pipe(takeUntil(this._destroy$))
                .subscribe(() => this.rebuildForNewContext());

        // Reclaim the realtime connection while the tab is hidden past a defined
        // window, re-establishing it on focus. Ably re-attaches every channel
        // automatically on reconnect, so callers need no changes.
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this._onVisibilityChange);
        }
    }

    /**
     * Tear down the current instance and signal re-attach subscribers to re-subscribe on the
     * freshly rebuilt one. destroy() runs FIRST (unconditionally) so a subscriber's re-subscribe
     * always lands on a new instance: laravel-echo caches channels by name and `.listen()`
     * binds additively, so re-attaching against a still-live socket would double-bind
     * and fire every realtime event twice. destroy() is a safe no-op when the instance is already
     * null (the usual logout case), so calling it here also covers the paths that clear the token
     * WITHOUT going through logout() (token-expiry via read(), or the 401 interceptor's direct
     * clear() on the auth pages) — SD-7778.
     */
    private rebuildForNewContext(): void {
        this.destroy();
        this._reestablished$.next();
    }

    public channel(channel: string) {
        const echoChannel = this.getEchoInstance().channel(channel);
        this._activeChannels.set(channel, { type: 'public', channel: echoChannel });
        return echoChannel;
    }

    public private(channel: string) {
        const echoChannel = this.getEchoInstance().private(channel);
        this._activeChannels.set(channel, { type: 'private', channel: echoChannel });
        return echoChannel;
    }

    public leave(channel: string) {
        try {
            const result = this.getEchoInstance().leave(channel);
            this._activeChannels.delete(channel);
            return result;
        } catch (error) {
            // Benign teardown race — caller still gets the rethrown error; keep a
            // dev-only breadcrumb. The !environment.production gate below keeps it
            // out of the prod console (SD-7666 removed SD-7544's global console mask).
            if (!environment.production) {
                console.error(`EchoService: Error leaving channel ${channel}:`, error);
            }
            throw error;
        }
    }

    public leaveChannel(channel: string) {
        try {
            const result = this.getEchoInstance().leaveChannel(channel);
            this._activeChannels.delete(channel);
            return result;
        } catch (error) {
            // Benign teardown race — caller still gets the rethrown error; keep a
            // dev-only breadcrumb. The !environment.production gate below keeps it
            // out of the prod console (SD-7666 removed SD-7544's global console mask).
            if (!environment.production) {
                console.error(`EchoService: Error leaving channel ${channel}:`, error);
            }
            throw error;
        }
    }

    public getSocketId() {
        if (this._echo && this._echo.socketId()) {
            return this._echo.socketId();
        }
        return null;
    }

    /**
     * Get list of active channels for debugging
     */
    public getActiveChannels(): string[] {
        return Array.from(this._activeChannels.keys());
    }

    /**
     * Get detailed information about active channels
     */
    public getChannelDetails(): any[] {
        return Array.from(this._activeChannels.entries()).map(([name, details]) => ({
            name,
            type: details.type,
            hasChannel: !!details.channel
        }));
    }

    /**
     * Force disconnect and destroy the Echo instance
     * Useful for testing and org changes
     */
    public destroy(): void {
        // Leave all active channels first
        const channelsToLeave = Array.from(this._activeChannels.keys());

        channelsToLeave.forEach(channelName => {
            try {
                this.leave(channelName);
            } catch (error) {
                // Benign teardown race — best-effort cleanup continues for the
                // remaining channels; dev-only breadcrumb.
                if (!environment.production) {
                    console.error(`EchoService: Error leaving channel ${channelName} during destroy:`, error);
                }
            }
        });

        // Disconnect and destroy Echo instance
        if (this._echo) {
            try {
                this._echo.disconnect();
            } catch (error) {
                this.reportError('EchoService: Error disconnecting Echo', error);
            }
            this._echo = null;
        }

        this._activeChannels.clear();
        this._isConnected$.next(false);
        // A rebuilt instance starts connected, never paused.
        this._paused = false;
    }

    ngOnDestroy(): void {
        this.cancelHiddenIdleDisconnect();
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this._onVisibilityChange);
        }
        this.destroy();
        this._destroy$.next();
        this._destroy$.complete();
        this._isConnected$.complete();
        this._reestablished$.complete();
    }

    /**
     * Close the underlying Ably connection while keeping the Echo instance and all
     * channel bindings intact, so a hidden/idle tab stops holding a concurrent
     * connection. No-op unless an active, un-paused connection exists.
     */
    public pauseConnection(): void {
        if (!this._echo || this._paused) {
            return;
        }

        const connection = this._echo.connector?.ably?.connection;
        if (!connection) {
            return;
        }

        this._paused = true;
        try {
            connection.close();
        } catch (error) {
            this.reportError('EchoService: Error pausing Ably connection', error);
        }
    }

    /**
     * Re-establish a connection previously paused by pauseConnection(). An explicit
     * close() leaves every channel `detached`, and Ably only re-attaches channels that
     * were interrupted (suspended), never ones that were closed on purpose, so each
     * detached channel is attached again here. Its `.listen()` subscriptions live on
     * the channel object and survive the detach, so callers need nothing. No-op unless
     * we paused the connection ourselves.
     */
    public resumeConnection(): void {
        if (!this._echo || !this._paused) {
            return;
        }

        const ably = this._echo.connector?.ably;
        this._paused = false;
        if (!ably?.connection) {
            return;
        }

        try {
            ably.connection.connect();
            const channels: Record<string, { state: string; attach: () => void }> = ably.channels?.all ?? {};
            Object.values(channels).forEach((channel) => {
                if (channel.state === 'detached') {
                    channel.attach();
                }
            });
        } catch (error) {
            this.reportError('EchoService: Error resuming Ably connection', error);
        }
    }

    private scheduleHiddenIdleDisconnect(): void {
        this.cancelHiddenIdleDisconnect();
        this._hiddenIdleTimer = setTimeout(() => {
            this._hiddenIdleTimer = null;
            this.pauseConnection();
        }, HIDDEN_IDLE_DISCONNECT_MS);
    }

    private cancelHiddenIdleDisconnect(): void {
        if (this._hiddenIdleTimer !== null) {
            clearTimeout(this._hiddenIdleTimer);
            this._hiddenIdleTimer = null;
        }
    }

    /**
     * Manual cleanup method for testing purposes
     * (Root services' ngOnDestroy is never called automatically)
     */
    public manualCleanup(): void {
        this.destroy();
    }

    private getEchoInstance() {
        // Lazily (re)build the instance. Channel tokens come from core's /broadcasting/auth,
        // signed with the server's Ably key and scoped to the one channel; the request carries
        // the CURRENT JWT, so a channel joined after an org switch authorizes under the new org
        // without rebuilding the whole instance — OrganizationBroadcastService leaves/re-joins
        // the org channel per OrgContextService.currentOrgId$ (SD-8086). The instance is torn
        // down only on logout, and rebuilt here on the next channel access.
        if (!this._echo) {
            // requestTokenFn replaces the connector's built-in authEndpoint request entirely:
            // it is the only path that carries the JWT, XSRF and site headers core requires.
            this._echo = new Echo({
                broadcaster: 'ably',
                requestTokenFn: async (channelName: string, existingToken: string) => {
                    const response = await fetch(environment.LegFiCoreApi + '/broadcasting/auth', {
                        method: 'POST',
                        body: JSON.stringify({ channel_name: channelName, token: existingToken }),
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'Authorization': 'Bearer ' + LegFiJwtService.raw(),
                            'X-XSRF-TOKEN': this._authService.getCookie('XSRF-TOKEN'),
                            'X-Legfi-Site-Id': environment.SiteId.toString(),
                        },
                    });
                    if (!response.ok) {
                        this.reportError(`EchoService: Authorization failed for channel: ${channelName}`);
                        throw new Error(`Authorization failed (${response.status})`);
                    }
                    return response.json();
                },
                useTls: true,
                disconnectedRetryTimeout: 5000,
                suspendedRetryTimeout: 15000,
            });

            // Set up connection monitoring
            this.setupConnectionMonitoring();
        }
        return this._echo;
    }

    private setupConnectionMonitoring(): void {
        const connection = this._echo?.connector?.ably?.connection;
        if (!connection) {
            return;
        }
        connection.on((change: { current: string; reason?: unknown }) => {
            if (change.current === 'connected') {
                this._isConnected$.next(true);
                return;
            }
            if (['disconnected', 'suspended', 'closed', 'failed'].includes(change.current)) {
                this._isConnected$.next(false);
            }
            if (change.current === 'failed') {
                this.reportError('EchoService: Ably connection failed', change.reason);
            }
        });
    }

    /**
     * Hook for actionable Echo errors. The Better Stack client-error sink was
     * removed (SD-7666); this now emits a dev-only breadcrumb (gated by
     * !environment.production), with the dedupe bookkeeping keeping a
     * reconnect/auth storm from flooding the dev console with the same message
     * within REPORT_DEDUPE_MS.
     */
    private reportError(message: string, error?: unknown): void {
        const now = Date.now();
        const lastReportedAt = this._recentReports.get(message);

        if (lastReportedAt !== undefined && now - lastReportedAt < REPORT_DEDUPE_MS) {
            return;
        }

        this.pruneRecentReports(now);
        this._recentReports.set(message, now);

        if (!environment.production) {
            console.error(message, error);
        }
    }

    private pruneRecentReports(now: number): void {
        for (const [message, reportedAt] of this._recentReports) {
            if (now - reportedAt >= REPORT_DEDUPE_MS) {
                this._recentReports.delete(message);
            }
        }
    }
}
